import { ColumnWithHideInfo } from '@/common/utils';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { TopologyWidgetPositionEnvData } from '@/dashboard/features/widgets/topologyWidget/types/topology.types';
import type { LegendSaveInfo } from '@/dashboard/features/dashboardLegend/domain/dashboardLegend.types';
import { GridSettingEnvValue } from '@/common/components/molecules/modernGrid/modernGrid.types';
import { ViewportTransform } from '@vue-flow/core';
import { WidgetChartType } from '@/dashboard/shared/types/widgets.types';
import { ClusterPositionEnvData } from '@/dashboard/features/widgets/cluster/widget/composables/usePosition';
import { ClusterAliasEnvData } from '@/dashboard/features/widgets/cluster/widget/composables/useAlias';
import { useDashboardUserEnvStore } from './dashboard-user-env';

export type TableOption = Omit<ColumnWithHideInfo, 'index'>;

export type WidgetEnvValue<ChartType extends WidgetChartType> = ChartType extends 'TABLE'
  ? TableOption[]
  : ChartType extends 'TREE_GRID'
    ? GridSettingEnvValue | undefined
    : ChartType extends 'STATUS'
      ? { isSortedBySeverity: boolean }
      : ChartType extends 'TOPOLOGY'
        ? boolean | TopologyWidgetPositionEnvData | ViewportTransform
        : ChartType extends 'LEGEND'
          ? LegendSaveInfo
          : ChartType extends 'CLUSTER'
            ? ClusterPositionEnvData | ClusterAliasEnvData
            : unknown;

// chartType에 따라 타입을 명확하게 강제하는 타입 설정
export type EnvConfig<ChartType extends WidgetChartType> = {
  chartType: ChartType;
  global: boolean;
  value: WidgetEnvValue<ChartType>;
};

type UserEnvKey = string;

type StringifiedUserEnvValue = string;

const stringifyEnvValue = <ChartType extends WidgetChartType>(
  envValue: WidgetEnvValue<ChartType>,
): StringifiedUserEnvValue => {
  if (envValue == null) {
    return '';
  }
  return JSON.stringify(envValue);
};

export const isMatchingChartType = <ChartType extends WidgetChartType>(
  value: EnvConfig<WidgetChartType>,
  chartType: ChartType,
): value is EnvConfig<ChartType> => {
  return value.chartType === chartType;
};

export const useDashboardEditUserEnvStore = defineStore('useDashboardEditUserEnvStore', () => {
  const dashboardUserEnvStore = useDashboardUserEnvStore();
  const editingUserEnvData = ref<Map<UserEnvKey, EnvConfig<WidgetChartType>>>(new Map());

  // dashboard edit mode 에서 사용되는 user env data
  const useEditingUserEnv = () => {
    const clearEditingUserEnvData = () => {
      editingUserEnvData.value = new Map();
    };

    const getEditingUserEnvData = <ChartType extends WidgetChartType>(
      key: string,
      chartType: EnvConfig<ChartType>['chartType'],
    ): EnvConfig<ChartType>['value'] | undefined => {
      const envValue = editingUserEnvData.value.get(key);
      if (!envValue) {
        return undefined;
      }
      if (isMatchingChartType(envValue, chartType)) {
        return envValue.value;
      }
      return undefined;
    };

    const setEditingUserEnvData = <ChartType extends WidgetChartType>(
      key: string,
      newValue: EnvConfig<ChartType>,
    ) => {
      if (!editingUserEnvData.value) {
        editingUserEnvData.value = new Map();
      }
      editingUserEnvData.value.set(key, newValue);
    };

    const mutateEditingEnvValue = async () => {
      if (editingUserEnvData.value.size === 0) {
        return;
      }
      const { global: globalEnv, custom: customEnv } = Array.from(editingUserEnvData.value).reduce<{
        global: { key: string; value: string }[];
        custom: { key: string; value: string }[];
      }>(
        (categoried, [key, { global, value }]) => {
          const stringValue = stringifyEnvValue(value);
          // NOTE: TreeGridWidgetV2 의 경우 ModernGrid 에서 env 를 기준으로 그리드 상태를 관리하므로 편집뷰에서 global env 로 저장된 데이터를 user env 로 동기화해야함
          const isTreeGrid = key.includes('GRIDSETTINGS');

          if (global) {
            categoried.global.push({ key, value: stringValue });
            if (isTreeGrid) {
              const customEnvKey = key.replace('global', 'user');
              categoried.custom.push({ key: customEnvKey, value: stringValue });
            }
          } else if (!isTreeGrid) {
            categoried.custom.push({ key, value: stringValue });
          }
          return categoried;
        },
        {
          global: [],
          custom: [],
        },
      );
      await dashboardUserEnvStore.setEnvValue(globalEnv, true);
      await dashboardUserEnvStore.setEnvValue(customEnv, false);

      clearEditingUserEnvData();
    };

    return {
      editingUserEnvData,
      clearEditingUserEnvData,
      getEditingUserEnvData,
      setEditingUserEnvData,
      mutateEditingEnvValue,
    };
  };

  // dashboard preview mode 에서 사용되는 user env data
  const usePreviewUserEnv = () => {
    const previewUserEnvData = ref<Map<UserEnvKey, EnvConfig<WidgetChartType>>>(new Map());

    const getPreviewUserEnvData = <ChartType extends WidgetChartType>(
      key: string,
      chartType: EnvConfig<ChartType>['chartType'],
    ): EnvConfig<ChartType>['value'] | undefined => {
      const envValue = previewUserEnvData.value.get(key);
      if (!envValue) {
        return undefined;
      }

      if (isMatchingChartType(envValue, chartType)) {
        return envValue.value;
      }

      return undefined;
    };

    const setPreviewUserEnvData = (newUserEnvData: {
      key: string;
      value: Omit<EnvConfig<WidgetChartType>, 'global'>;
      global?: boolean;
    }) => {
      if (!previewUserEnvData.value) {
        previewUserEnvData.value = new Map();
      }

      previewUserEnvData.value.set(newUserEnvData.key, {
        ...newUserEnvData.value,
        global: newUserEnvData.global ?? true,
      });
    };

    const clearPreviewUserEnvData = () => {
      previewUserEnvData.value = new Map();
    };

    // widget setting 창에서 수정 후 save 버튼 눌렀을때 editingUserEnvData에 previewUserEnvData 동기화
    const syncEditingUserEnvData = async () => {
      if (previewUserEnvData.value.size === 0) {
        return;
      }

      previewUserEnvData.value.forEach((value, key) => {
        editingUserEnvData.value.set(key, value);
      });

      clearPreviewUserEnvData();
    };

    return {
      previewUserEnvData,
      getPreviewUserEnvData,
      setPreviewUserEnvData,
      clearPreviewUserEnvData,
      syncEditingUserEnvData,
    };
  };

  return {
    ...useEditingUserEnv(),
    ...usePreviewUserEnv(),
  };
});
